import moment from "moment-timezone";
import { useListMerchValues, RiskMerchValue } from "@/lib/api/risk/merchValues";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { CommodityOptions } from "@/lib/api/commodity";

// ---------------------------------------------------------------------------
// Commodity label lookup
// ---------------------------------------------------------------------------

const commodityLabelMap = new Map<string, string>(
  CommodityOptions.map((o) => [o.value as string, o.label]),
);

function getCommodityLabel(commodityValue: string): string {
  return commodityLabelMap.get(commodityValue) || commodityValue;
}

// ---------------------------------------------------------------------------
// Archived card — read-only display
// ---------------------------------------------------------------------------

function formatDate(isoString: string): string {
  return moment.tz(isoString, CommonDestinationTimezone).format("MMM Do, YYYY");
}

function formatRangeDate(isoString: string): string {
  return moment.tz(isoString, CommonDestinationTimezone).format("MMM Do");
}

interface ArchivedCardProps {
  item: RiskMerchValue;
}

function ArchivedCard({ item }: ArchivedCardProps) {
  const dateLabel = `${formatRangeDate(item.range_start)} → ${formatRangeDate(item.range_end)}`;
  const zoneName = item.MarketZone?.dtn_market_zone ?? "Unknown Zone";
  const commodityLabel = getCommodityLabel(item.commodity);

  return (
    <div
      data-testid="merch-value-archived-card"
      className="flex flex-col gap-1 rounded border border-border bg-muted/30 px-3 py-2 min-w-[180px] max-w-[240px]"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide truncate">
          {commodityLabel}
        </span>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          CY {item.crop_year}
        </span>
      </div>
      <span className="text-xs text-muted-foreground truncate">{zoneName}</span>
      <span className="text-xs font-medium text-foreground">{dateLabel}</span>
      {item.value && (
        <span className="text-sm font-bold leading-tight truncate">{item.value}</span>
      )}
      {item.note && (
        <span className="text-[11px] text-muted-foreground leading-tight truncate">
          {item.note}
        </span>
      )}
      {item.archived_at && (
        <span className="text-[11px] text-muted-foreground leading-tight mt-0.5">
          Archived {formatDate(item.archived_at)}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

export function MerchValuesHistoryTab() {
  const archivedQuery = useListMerchValues({ archived: "true" });

  const items: RiskMerchValue[] = archivedQuery.data?.data || [];

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {archivedQuery.isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No archived merch values.</p>
      ) : (
        <div className="flex flex-row flex-wrap gap-3">
          {items.map((item) => (
            <ArchivedCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
